import {Button, Group, Paper, Progress, Stack, Text} from '@mantine/core';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {NewsletterService, type NewsletterSendStatus} from '../../client';
import {notifications} from '@mantine/notifications';
import {formatRequestError} from '../../utils/formatRequestError';

type NewsletterSendProgressProps = {
    status: NewsletterSendStatus | undefined;
    isAdmin?: boolean;
};

export function NewsletterSendProgress({status, isAdmin = false}: NewsletterSendProgressProps) {
    const queryClient = useQueryClient();

    const cancelMutation = useMutation({
        mutationFn: NewsletterService.cancelNewsletterSendApiV1NewsletterSendCancelPost,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['newsletter-send-status']});
            notifications.show({
                color: 'blue',
                title: 'Cancellation requested',
                message: 'The newsletter send will stop after the current mail.',
            });
        },
        onError: (error: unknown) => {
            notifications.show({
                color: 'red',
                title: 'Could not cancel send',
                message: formatRequestError(error, 'Request failed'),
            });
        },
    });

    if (!status?.sending) {
        return null;
    }

    const remaining = Math.max(status.total - status.sent, 0);
    const progress = status.total > 0
        ? Math.round((status.sent / status.total) * 100)
        : 0;

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="xs">
                <Text size="sm" fw={600}>Newsletter send in progress</Text>
                <Progress value={progress} size="lg" radius="xl" />
                <Text size="sm" c="dimmed">
                    {status.sent} of {status.total} sent ({remaining} remaining)
                </Text>
                {isAdmin ? (
                    <Group gap="xs">
                        <Button
                            color="red"
                            variant="outline"
                            loading={cancelMutation.isPending}
                            disabled={cancelMutation.isSuccess}
                            onClick={() => cancelMutation.mutate()}
                        >
                            Cancel send
                        </Button>
                    </Group>
                ) : null}
            </Stack>
        </Paper>
    );
}
